/**
 * The application (pipeline) stage machine, on the client, and the words for its
 * seven stages.
 *
 * The pipeline analogue of `web/src/recruiting/requisition.ts`, and treated the same
 * way: the transition set a card offers is **derived from this table**, so an illegal
 * move has no button rather than a disabled one, and the server's `409
 * WORKFORCE_INVALID_STATE_TRANSITION` — carrying `details.allowed_transitions[]` — is
 * what actually decides. This copy exists so a card can offer the legal moves *before*
 * a refusal; the refusal's list is authoritative and is printed verbatim when the two
 * ever disagree.
 *
 * ── The machine (transcribed from `openapi.yaml`'s transition description) ────
 *
 *   applied    → screening | rejected | withdrawn
 *   screening  → interview | rejected | withdrawn
 *   interview  → offer     | rejected | withdrawn
 *   offer      → rejected  | withdrawn
 *   hired / rejected / withdrawn — terminal, no move out
 *
 * ── `hired` is never a transition target ─────────────────────────────────────
 *
 * `hired` appears in no row's allowed set: the `/transition` endpoint **refuses**
 * `→ hired` (`409` with a hint to use the Phase 2 hire action), so this table never
 * offers it and this client never calls transition with it. `offer` is the one stage
 * a hire is legal from — the screen shows a **disabled** "Hire" affordance there with
 * a tooltip pointing at the separate action, and never issues the call. The table is
 * transcribed rather than imported, for the same reason `requisition.ts` gives — the
 * SPA bundle is served to browsers, and a `web/ → src/modules/` import drags a `pg`
 * handle into one.
 */

import type { MessageKey } from '../i18n/messages';
import type { Severity } from '../states/icons';
import type { ApplicationStage } from '../api/recruiting';

export { APPLICATION_STAGES } from '../api/recruiting';
export type { ApplicationStage } from '../api/recruiting';

export function isApplicationStage(value: unknown): value is ApplicationStage {
  return (
    typeof value === 'string' &&
    (
      [
        'applied',
        'screening',
        'interview',
        'offer',
        'hired',
        'rejected',
        'withdrawn',
      ] as readonly string[]
    ).includes(value)
  );
}

/**
 * Every stage has a row, the terminal three included, whose row is the empty list.
 * An absent key and an empty list are different statements; keeping the empty rows
 * makes "is this terminal" a fact read off the table rather than an omission. Note
 * that `hired` is a target in **no** row — it is not reachable through `/transition`.
 */
export const APPLICATION_TRANSITIONS: Readonly<
  Record<ApplicationStage, readonly ApplicationStage[]>
> = {
  applied: ['screening', 'rejected', 'withdrawn'],
  screening: ['interview', 'rejected', 'withdrawn'],
  interview: ['offer', 'rejected', 'withdrawn'],
  offer: ['rejected', 'withdrawn'],
  hired: [],
  rejected: [],
  withdrawn: [],
};

export function stageTransitions(from: ApplicationStage): readonly ApplicationStage[] {
  return APPLICATION_TRANSITIONS[from];
}

/** `true` for exactly the stages with no outgoing edge. Derived, never listed. */
export function isTerminalStage(stage: ApplicationStage): boolean {
  return APPLICATION_TRANSITIONS[stage].length === 0;
}

/**
 * The moves this stage does **not** allow — the complement, computed rather than
 * written down, so a stage cannot be missing from both lists. For the tests; no
 * component renders it. `hired` is in every stage's forbidden list, which is the
 * property the pipeline test asserts (`→ hired` is absent from every transition set).
 */
export function forbiddenStageTransitions(from: ApplicationStage): readonly ApplicationStage[] {
  const allowed = new Set<ApplicationStage>(APPLICATION_TRANSITIONS[from]);
  return (Object.keys(APPLICATION_TRANSITIONS) as ApplicationStage[]).filter(
    (stage) => !allowed.has(stage),
  );
}

/** `offer` is the one stage a hire is legal from — where the disabled affordance shows. */
export function isHireableStage(stage: ApplicationStage): boolean {
  return stage === 'offer';
}

/** The name of a stage, for the column heading and the card's badge. */
export const STAGE_LABEL: Readonly<Record<ApplicationStage, MessageKey>> = {
  applied: 'recruiting.pipeline.stage.applied',
  screening: 'recruiting.pipeline.stage.screening',
  interview: 'recruiting.pipeline.stage.interview',
  offer: 'recruiting.pipeline.stage.offer',
  hired: 'recruiting.pipeline.stage.hired',
  rejected: 'recruiting.pipeline.stage.rejected',
  withdrawn: 'recruiting.pipeline.stage.withdrawn',
};

/**
 * What pressing a move control **does**, as a verb — not the name of the stage.
 * The badge uses `STAGE_LABEL`; the transition button this.
 */
export const STAGE_MOVE_LABEL: Readonly<Record<ApplicationStage, MessageKey>> = {
  applied: 'recruiting.pipeline.move.applied',
  screening: 'recruiting.pipeline.move.screening',
  interview: 'recruiting.pipeline.move.interview',
  offer: 'recruiting.pipeline.move.offer',
  hired: 'recruiting.pipeline.move.hired',
  rejected: 'recruiting.pipeline.move.rejected',
  withdrawn: 'recruiting.pipeline.move.withdrawn',
};

/**
 * The badge severity a stage is drawn at — how far along the pipeline the card sits.
 * Icon and text carry it, as everywhere; colour is the third channel (DESIGN.md §4).
 */
export const STAGE_BADGE: Readonly<Record<ApplicationStage, Severity>> = {
  applied: 'info',
  screening: 'info',
  interview: 'medium',
  offer: 'high',
  hired: 'low',
  rejected: 'low',
  withdrawn: 'low',
};
